import { useEffect, memo, useContext } from 'react';
import {
    Card,
    Grid,
    Stack,
    Box,
    Typography,
    Divider,
    TextField,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Button,
    Collapse,
    Paper,
} from '@mui/material';
import MDBox from 'components/MDBox';
import ViewToggle from 'layouts/purchases/pages/allLeads/layout/Header/Components/ViewToggle.js';
import ViewSwitch from 'layouts/purchases/pages/allLeads/layout/Header/Components/ViewSwitch.js';
import Aaro from 'assets/images/ahsaplar2.png';
import MDAvatar from 'components/MDAvatar';
import { isMobile } from 'helpers/smallCodes';
import { AllPurchasesContext } from 'layouts/purchases/pages/allLeads/index.js';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SortIcon from '@mui/icons-material/Sort';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useState, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';

// Responsive yardımcı fonksiyon
const getHeaderConfig = () => {
    const width = window.innerWidth;
    return {
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
        isDesktop: width > 1024,
    };
};

const Header = memo(function HeaderPre({ children }) {
    const {
        search,
        setSearch,
        refetch,
        statusFilter,
        setStatusFilter,
        paymentMethodFilter,
        setPaymentMethodFilter,
        dateRange,
        setDateRange,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
    } = useContext(AllPurchasesContext);

    const [searchValue, setSearchValue] = useState('');
    const [headerConfig] = useState(getHeaderConfig());
    const [showFilters, setShowFilters] = useState(false);

    // Local state for pending filter changes
    const [pendingFilters, setPendingFilters] = useState({
        statusFilter,
        paymentMethodFilter,
        dateRange,
    });

    // Sync pending filters with actual filters when they change
    useEffect(() => {
        setPendingFilters({
            statusFilter,
            paymentMethodFilter,
            dateRange,
        });
    }, [statusFilter, paymentMethodFilter, dateRange]);

    // Check if there are pending changes
    const hasPendingChanges = () => {
        return (
            pendingFilters.statusFilter !== statusFilter ||
            pendingFilters.paymentMethodFilter !== paymentMethodFilter ||
            pendingFilters.dateRange.preset !== dateRange.preset ||
            pendingFilters.dateRange.startDate !== dateRange.startDate ||
            pendingFilters.dateRange.endDate !== dateRange.endDate
        );
    };

    // Apply pending filters
    const applyFilters = () => {
        setStatusFilter(pendingFilters.statusFilter);
        setPaymentMethodFilter(pendingFilters.paymentMethodFilter);
        setDateRange(pendingFilters.dateRange);
    };

    // Reset pending filters to current state
    const resetPendingFilters = () => {
        setPendingFilters({
            statusFilter,
            paymentMethodFilter,
            dateRange,
        });
    };

    const handleSearch = () => {
        setSearch(searchValue);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    // Filter reset function
    const resetFilters = () => {
        const newStatusFilter = 'active'; // Yeni default: hazırlanıyor, beklemede, tamamlandı
        const newPaymentMethodFilter = 'all';
        const newDateRange = {
            startDate: null,
            endDate: null,
            preset: '15',
        };

        setStatusFilter(newStatusFilter);
        setPaymentMethodFilter(newPaymentMethodFilter);
        setDateRange(newDateRange);
        setPendingFilters({
            statusFilter: newStatusFilter,
            paymentMethodFilter: newPaymentMethodFilter,
            dateRange: newDateRange,
        });
        setSearch('');
        setSearchValue('');
        setSortBy('date_created');
        setSortOrder('desc');
    };

    // Count active filters
    const getActiveFilterCount = () => {
        let count = 0;
        if (statusFilter !== 'active') count++; // active artık default olduğu için
        if (paymentMethodFilter !== 'all') count++;
        if (dateRange.startDate && dateRange.endDate) count++;
        if (search.trim()) count++;
        return count;
    };

    useEffect(() => console.log('Header Rendered'));

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
            <MDBox position="relative" mb={5}>
                <HeaderGradiant />
                <Card
                    sx={{
                        position: 'relative',
                        mt: -8,
                        mx: 3,
                        py: 2,
                        px: 2,
                    }}
                >
                    <Grid container spacing={3} sx={{ alignItems: 'center' }}>
                        <Grid
                            style={{ flexGrow: 1 }}
                            size={{
                                xs: 12,
                                sm: 6,
                                lg: 6,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: headerConfig.isMobile ? 'center' : 'flex-start',
                                    height: '100%',
                                    mt: 0.5,
                                    lineHeight: 1,
                                }}
                            >
                                <MDAvatar
                                    src={Aaro}
                                    alt="profile-image"
                                    size={headerConfig.isMobile ? 'lg' : 'xl'}
                                    shadow="sm"
                                    variant="square"
                                />
                                <Box
                                    sx={{
                                        ml: 2,
                                        height: '100%',
                                        mt: 0.5,
                                        lineHeight: 1,
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 'medium',
                                            mb: 1,
                                            fontSize: headerConfig.isMobile
                                                ? '1.1rem'
                                                : headerConfig.isTablet
                                                ? '1.25rem'
                                                : '1.5rem',
                                            fontFamily: '"Inter", sans-serif',
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {headerConfig.isMobile ? 'Siparişler' : 'WooCommerce Siparişleri'}
                                    </Typography>
                                    <Stack spacing={1} direction="row" sx={{ alignItems: 'center' }}>
                                        <ViewToggle />
                                        {!headerConfig.isMobile && (
                                            <>
                                                <Divider orientation="vertical" flexItem />
                                                <ViewSwitch />
                                            </>
                                        )}
                                    </Stack>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Search and Controls Section */}
                        <Grid
                            sx={{ alignItems: 'center' }}
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 6,
                            }}
                        >
                            <MDBox display="flex" justifyContent="flex-end" width="100%" alignItems="center" gap={1}>
                                {!headerConfig.isMobile && (
                                    <>
                                        <TextField
                                            size="small"
                                            variant="outlined"
                                            placeholder="Sipariş ara..."
                                            value={searchValue}
                                            onChange={(e) => setSearchValue(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            sx={{
                                                minWidth: headerConfig.isTablet ? 200 : 240,
                                                '& .MuiOutlinedInput-root': {
                                                    height: headerConfig.isTablet ? 32 : 36,
                                                    borderRadius: 1.5,
                                                    backgroundColor: '#FFFFFF',
                                                    border: '1px solid #D1D5DB',
                                                    fontFamily: '"Inter", sans-serif',
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': {
                                                        borderColor: '#9CA3AF',
                                                    },
                                                    '&.Mui-focused': {
                                                        borderColor: '#10B981',
                                                        backgroundColor: '#FFFFFF',
                                                        boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.1)',
                                                    },
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: headerConfig.isTablet ? '4px 10px' : '6px 12px',
                                                    fontSize: headerConfig.isTablet ? '0.8rem' : '0.875rem',
                                                    fontWeight: '400',
                                                    color: '#374151',
                                                    fontFamily: '"Inter", sans-serif',
                                                    '&::placeholder': {
                                                        color: '#9CA3AF',
                                                        opacity: 1,
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleSearch}
                                                        edge="end"
                                                        sx={{
                                                            color: '#9CA3AF',
                                                            '&:hover': {
                                                                color: '#10B981',
                                                                bgcolor: 'transparent',
                                                            },
                                                        }}
                                                    >
                                                        <SearchIcon fontSize="small" />
                                                    </IconButton>
                                                ),
                                            }}
                                        />

                                        {/* Filter Toggle Button */}
                                        <Tooltip title="Filtreleri Aç/Kapat" arrow>
                                            <IconButton
                                                onClick={() => setShowFilters(!showFilters)}
                                                sx={{
                                                    ml: 1,
                                                    width: headerConfig.isTablet ? 32 : 36,
                                                    height: headerConfig.isTablet ? 32 : 36,
                                                    bgcolor: showFilters ? '#0EA5E9' : '#F3F4F6',
                                                    color: showFilters ? 'white' : '#6B7280',
                                                    borderRadius: 1.5,
                                                    position: 'relative',
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': {
                                                        bgcolor: showFilters ? '#0284C7' : '#E5E7EB',
                                                    },
                                                }}
                                            >
                                                <TuneIcon fontSize="small" />
                                                {getActiveFilterCount() > 0 && (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -4,
                                                            right: -4,
                                                            width: 16,
                                                            height: 16,
                                                            bgcolor: '#EF4444',
                                                            color: 'white',
                                                            borderRadius: '50%',
                                                            fontSize: '0.6rem',
                                                            fontWeight: 'bold',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontFamily: '"Inter", sans-serif',
                                                        }}
                                                    >
                                                        {getActiveFilterCount()}
                                                    </Box>
                                                )}
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Verileri Yenile" arrow>
                                            <IconButton
                                                onClick={() => refetch()}
                                                sx={{
                                                    ml: 1,
                                                    width: headerConfig.isTablet ? 32 : 36,
                                                    height: headerConfig.isTablet ? 32 : 36,
                                                    bgcolor: '#10B981',
                                                    color: 'white',
                                                    borderRadius: 1.5,
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': {
                                                        bgcolor: '#059669',
                                                    },
                                                }}
                                            >
                                                <RefreshIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                            </MDBox>
                        </Grid>
                    </Grid>

                    {/* Advanced Filters Panel */}
                    <Collapse in={showFilters}>
                        <Paper
                            elevation={0}
                            sx={{
                                mt: 3,
                                p: 3,
                                borderRadius: 2,
                                border: '1px solid #E5E7EB',
                                backgroundColor: '#F8FAFC',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <FilterListIcon sx={{ color: '#6B7280', mr: 1 }} />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: '600',
                                        color: '#374151',
                                        fontFamily: '"Inter", sans-serif',
                                        fontSize: '1rem',
                                    }}
                                >
                                    Gelişmiş Filtreler
                                </Typography>
                                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                                    {hasPendingChanges() && (
                                        <>
                                            <Button
                                                size="small"
                                                onClick={resetPendingFilters}
                                                sx={{
                                                    color: '#6B7280',
                                                    fontWeight: '500',
                                                    textTransform: 'none',
                                                    fontSize: '0.875rem',
                                                    '&:hover': {
                                                        bgcolor: '#F3F4F6',
                                                    },
                                                }}
                                            >
                                                İptal
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={applyFilters}
                                                sx={{
                                                    bgcolor: '#10B981',
                                                    color: 'white',
                                                    fontWeight: '500',
                                                    textTransform: 'none',
                                                    fontSize: '0.875rem',
                                                    '&:hover': {
                                                        bgcolor: '#059669',
                                                    },
                                                }}
                                            >
                                                Filtrele
                                            </Button>
                                        </>
                                    )}
                                    <Button
                                        size="small"
                                        onClick={resetFilters}
                                        sx={{
                                            color: '#6B7280',
                                            fontWeight: '500',
                                            textTransform: 'none',
                                            fontSize: '0.875rem',
                                            '&:hover': {
                                                bgcolor: '#F3F4F6',
                                            },
                                        }}
                                    >
                                        Filtreleri Temizle
                                    </Button>
                                </Box>
                            </Box>

                            <Grid container spacing={3}>
                                {/* Status Filter */}
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontFamily: '"Inter", sans-serif' }}>
                                            Sipariş Durumu
                                        </InputLabel>
                                        <Select
                                            value={pendingFilters.statusFilter}
                                            onChange={(e) =>
                                                setPendingFilters({
                                                    ...pendingFilters,
                                                    statusFilter: e.target.value,
                                                })
                                            }
                                            label="Sipariş Durumu"
                                            startAdornment={<AssignmentIcon sx={{ color: '#6B7280', mr: 1 }} />}
                                            sx={{
                                                fontFamily: '"Inter", sans-serif',
                                                '& .MuiSelect-select': {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                },
                                            }}
                                        >
                                            <MenuItem value="active">
                                                Aktif Siparişler (Hazırlanıyor, Ödeme Bekleniyor, Tamamlandı)
                                            </MenuItem>
                                            <MenuItem value="all">Tümü</MenuItem>
                                            <MenuItem value="processing">Hazırlanıyor</MenuItem>
                                            <MenuItem value="completed">Tamamlandı</MenuItem>
                                            <MenuItem value="pending">Ödeme Bekleniyor</MenuItem>
                                            <MenuItem value="on-hold">Bekletildi</MenuItem>
                                            <MenuItem value="cancelled">İptal Edildi</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Payment Method Filter */}
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontFamily: '"Inter", sans-serif' }}>
                                            Ödeme Yöntemi
                                        </InputLabel>
                                        <Select
                                            value={pendingFilters.paymentMethodFilter}
                                            onChange={(e) =>
                                                setPendingFilters({
                                                    ...pendingFilters,
                                                    paymentMethodFilter: e.target.value,
                                                })
                                            }
                                            label="Ödeme Yöntemi"
                                            startAdornment={<PaymentIcon sx={{ color: '#6B7280', mr: 1 }} />}
                                            sx={{
                                                fontFamily: '"Inter", sans-serif',
                                                '& .MuiSelect-select': {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">Tümü</MenuItem>
                                            <MenuItem value="bacs">Havale/EFT</MenuItem>
                                            <MenuItem value="cod">Kapıda Ödeme</MenuItem>
                                            <MenuItem value="paytr">PayTR</MenuItem>
                                            <MenuItem value="stripe">Kredi Kartı</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Date Range Preset */}
                                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ fontFamily: '"Inter", sans-serif' }}>
                                            Tarih Aralığı
                                        </InputLabel>
                                        <Select
                                            value={pendingFilters.dateRange.preset}
                                            onChange={(e) => {
                                                const preset = e.target.value;
                                                setPendingFilters({
                                                    ...pendingFilters,
                                                    dateRange: {
                                                        ...pendingFilters.dateRange,
                                                        preset,
                                                        startDate:
                                                            preset === 'custom'
                                                                ? pendingFilters.dateRange.startDate
                                                                : null,
                                                        endDate:
                                                            preset === 'custom'
                                                                ? pendingFilters.dateRange.endDate
                                                                : null,
                                                    },
                                                });
                                            }}
                                            label="Tarih Aralığı"
                                            startAdornment={<CalendarTodayIcon sx={{ color: '#6B7280', mr: 1 }} />}
                                            sx={{
                                                fontFamily: '"Inter", sans-serif',
                                                '& .MuiSelect-select': {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                },
                                            }}
                                        >
                                            <MenuItem value="7">Son 7 Gün</MenuItem>
                                            <MenuItem value="15">Son 15 Gün</MenuItem>
                                            <MenuItem value="30">Son 30 Gün</MenuItem>
                                            <MenuItem value="90">Son 90 Gün</MenuItem>
                                            <MenuItem value="custom">Özel Aralık</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Custom Date Range */}
                                {pendingFilters.dateRange.preset === 'custom' && (
                                    <>
                                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                            <DatePicker
                                                label="Başlangıç Tarihi"
                                                value={pendingFilters.dateRange.startDate}
                                                onChange={(newValue) => {
                                                    setPendingFilters({
                                                        ...pendingFilters,
                                                        dateRange: {
                                                            ...pendingFilters.dateRange,
                                                            startDate: newValue,
                                                        },
                                                    });
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        fullWidth: true,
                                                        sx: {
                                                            '& .MuiInputBase-root': {
                                                                fontFamily: '"Inter", sans-serif',
                                                            },
                                                        },
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                                            <DatePicker
                                                label="Bitiş Tarihi"
                                                value={pendingFilters.dateRange.endDate}
                                                onChange={(newValue) => {
                                                    setPendingFilters({
                                                        ...pendingFilters,
                                                        dateRange: {
                                                            ...pendingFilters.dateRange,
                                                            endDate: newValue,
                                                        },
                                                    });
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        fullWidth: true,
                                                        sx: {
                                                            '& .MuiInputBase-root': {
                                                                fontFamily: '"Inter", sans-serif',
                                                            },
                                                        },
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>

                            {/* Active Filters Display */}
                            {getActiveFilterCount() > 0 && (
                                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #E5E7EB' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: '500',
                                            color: '#6B7280',
                                            mb: 2,
                                            fontFamily: '"Inter", sans-serif',
                                        }}
                                    >
                                        Aktif Filtreler:
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {statusFilter !== 'active' && (
                                            <Chip
                                                label={`Durum: ${
                                                    statusFilter === 'all'
                                                        ? 'Tümü'
                                                        : statusFilter === 'active'
                                                        ? 'Aktif Siparişler'
                                                        : statusFilter === 'processing'
                                                        ? 'Hazırlanıyor'
                                                        : statusFilter === 'completed'
                                                        ? 'Tamamlandı'
                                                        : statusFilter === 'pending'
                                                        ? 'Ödeme Bekleniyor'
                                                        : statusFilter === 'on-hold'
                                                        ? 'Bekletildi'
                                                        : statusFilter === 'cancelled'
                                                        ? 'İptal Edildi'
                                                        : statusFilter
                                                }`}
                                                onDelete={() => setStatusFilter('active')}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                        {paymentMethodFilter !== 'all' && (
                                            <Chip
                                                label={`Ödeme: ${
                                                    paymentMethodFilter === 'bacs'
                                                        ? 'Havale/EFT'
                                                        : paymentMethodFilter === 'cod'
                                                        ? 'Kapıda Ödeme'
                                                        : paymentMethodFilter === 'paytr'
                                                        ? 'PayTR'
                                                        : paymentMethodFilter === 'stripe'
                                                        ? 'Kredi Kartı'
                                                        : paymentMethodFilter
                                                }`}
                                                onDelete={() => setPaymentMethodFilter('all')}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                        {search.trim() && (
                                            <Chip
                                                label={`Arama: "${search}"`}
                                                onDelete={() => {
                                                    setSearch('');
                                                    setSearchValue('');
                                                }}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                        {dateRange.startDate && dateRange.endDate && (
                                            <Chip
                                                label={`Tarih: ${new Date(dateRange.startDate).toLocaleDateString(
                                                    'tr-TR'
                                                )} - ${new Date(dateRange.endDate).toLocaleDateString('tr-TR')}`}
                                                onDelete={() => {
                                                    const newDateRange = {
                                                        startDate: null,
                                                        endDate: null,
                                                        preset: '15',
                                                    };
                                                    setDateRange(newDateRange);
                                                    setPendingFilters({
                                                        ...pendingFilters,
                                                        dateRange: newDateRange,
                                                    });
                                                }}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            )}
                        </Paper>
                    </Collapse>

                    {children}
                </Card>
            </MDBox>
        </LocalizationProvider>
    );

    function HeaderGradiant() {
        return (
            <MDBox
                display="flex"
                alignItems="center"
                position="relative"
                minHeight="8rem"
                borderRadius="xl"
                sx={{
                    background: 'linear-gradient(135deg, #059669 0%, #047857 50%, #065f46 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: '50%',
                    overflow: 'hidden',
                }}
            />
        );
    }
});

export default Header;
